import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Check, Mail } from "lucide-react";
import { api, formatApiErrorDetail, SUBSTACK_URL, DONATE_URL } from "@/lib/api";
import { RevealLines, FadeUp } from "@/components/Reveal";
import { SocialLinks } from "@/components/SocialLinks";
import Seo from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SUBJECTS = ["General", "Story tip", "Press / Media", "Correspondent inquiry", "Volunteer", "Other"];
const EMAIL = "editor@thecanary.report";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/messages", form);
      setDone(true);
      toast.success("Message sent", { description: "We'll get back to you soon." });
      setForm({ name: "", email: "", subject: "General", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact" description="Story tips, press inquiries, partnerships, or a note from the community — reach The Canary Report directly." />
      <section className="pt-[120px] pb-12 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
        <p className="label text-ink/50 mb-6">Contact</p>
        <RevealLines as="h1"
          className="font-display font-black text-[13vw] lg:text-[8vw] leading-[0.85] tracking-tight"
          lines={["Get in", "touch."]}
        />
        <FadeUp delay={0.5}>
          <p className="mt-8 max-w-xl text-lg text-ink/70 leading-relaxed">
            Story tips, press inquiries, partnerships, or a note from the community — we read
            everything. Reach us directly at{" "}
            <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="text-ink font-semibold link-underline pb-0.5">{EMAIL}</a>.
          </p>
        </FadeUp>
      </section>

      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 pb-24 grid lg:grid-cols-12 gap-12">
        {/* Left: quick contact */}
        <FadeUp className="lg:col-span-4 space-y-8">
          <div className="border border-ink p-8">
            <span className="h-11 w-11 rounded-full bg-canary grid place-items-center mb-5"><Mail size={18} /></span>
            <p className="label text-ink/50 mb-2">Email</p>
            <a href={`mailto:${EMAIL}`} className="font-display text-2xl font-bold link-underline pb-0.5">{EMAIL}</a>
          </div>
          <div className="space-y-3">
            <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-substack" className="flex items-center justify-between border border-ink/20 px-6 py-4 hover:border-ink transition-colors group">
              <span className="label">Read on Substack</span><ArrowUpRight size={16} className="group-hover:text-canary" />
            </a>
            <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-donate" className="flex items-center justify-between border border-ink/20 px-6 py-4 hover:border-ink transition-colors group">
              <span className="label">Support our work</span><ArrowUpRight size={16} className="group-hover:text-canary" />
            </a>
          </div>
          <p className="text-ink/50 text-sm">TheCanary.Report · Denver, Colorado</p>
          <div data-testid="contact-socials" className="pt-2">
            <p className="label text-ink/50 mb-3">Follow The Canary Report</p>
            <SocialLinks group="brand" variant="light" testidPrefix="contact-social" />
          </div>
        </FadeUp>

        {/* Right: form */}
        <div className="lg:col-span-7 lg:col-start-6">
          {done ? (
            <div data-testid="contact-success" className="border border-ink p-10 flex flex-col items-start gap-4">
              <span className="h-12 w-12 rounded-full bg-canary grid place-items-center"><Check /></span>
              <h3 className="font-display text-3xl font-bold">Message sent.</h3>
              <p className="text-ink/70">Thanks for reaching out — we'll be in touch.</p>
              <button data-testid="contact-another-btn" onClick={() => setDone(false)} className="label link-underline pb-1 mt-2">Send another</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label className="label" htmlFor="name">Name *</Label>
                  <Input id="name" required value={form.name} onChange={set("name")} data-testid="contact-name" className="mt-2 rounded-none border-ink h-12" placeholder="Your name" />
                </div>
                <div>
                  <Label className="label" htmlFor="email">Email *</Label>
                  <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" className="mt-2 rounded-none border-ink h-12" placeholder="you@email.com" />
                </div>
              </div>
              <div>
                <Label className="label">Subject</Label>
                <Select value={form.subject} onValueChange={(v) => setForm((f) => ({ ...f, subject: v }))}>
                  <SelectTrigger data-testid="contact-subject" className="mt-2 rounded-none border-ink h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s} data-testid={`subject-${s}`}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label" htmlFor="message">Message *</Label>
                <Textarea id="message" required rows={6} value={form.message} onChange={set("message")} data-testid="contact-message" className="mt-2 rounded-none border-ink" placeholder="How can we help?" />
              </div>
              <Button type="submit" disabled={sending} data-testid="contact-submit" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink h-14 px-9 label">
                {sending ? "Sending…" : <>Send message <ArrowUpRight size={16} className="ml-2" /></>}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
